import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  CircularProgress,
  InputAdornment,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DeleteIcon from "@mui/icons-material/Delete";
// import SortBy from "../Shared/SortBy"; // Import the SortBy component
import VideoPlayerModal from "../Shared/VideoPlayerModal"; // Import the VideoPlayerModal component

// Import API helper functions
import {
  makeApiRequest,
  fetchVideos,
  fetchScripts,
  deleteVideo,
  batchDeleteVideos,
} from "../../services/api";
import { clearTokens } from "../../utils/auth";
import { API_ENDPOINTS } from "../../config/api";

// Utility function for debouncing API calls
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

function Videos() {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalVideos, setTotalVideos] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [sortCriteria, setSortCriteria] = useState({
    type: "date",
    value: "Newest",
  });

  // State for video player modal
  const [playerOpen, setPlayerOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);

  // State for selected videos and processing status
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [processingStatus, setProcessingStatus] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // State for confirmation dialogs
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    videoId: null,
    videoTitle: "",
    isBatch: false,
  });

  const [isDeleting, setIsDeleting] = useState(false);

  // Debounced search function to reduce unnecessary API calls
  const debouncedSearch = useCallback(
    debounce((page, search) => {
      loadVideos(page, search);
    }, 500),
    []
  );

  // Function to handle authentication errors
  const handleAuthError = (error) => {
    if (
      error.message &&
      (error.message.includes("Authentication required") ||
        error.message.includes("Invalid token") ||
        error.message.includes("Token expired") ||
        error.message.toLowerCase().includes("unauthorized") ||
        error.message.toLowerCase().includes("authentication"))
    ) {
      // Clear tokens and redirect to login
      clearTokens();
      setSnackbar({
        open: true,
        message: "Your session has expired. Please log in again.",
        severity: "error",
      });
      // Redirect to login after a short delay
      setTimeout(() => navigate("/login"), 2000);
    }
  };

  // Function to load videos from API
  const loadVideos = async (page, search = "") => {
    try {
      setLoading(true);
      setError(null);

      // Use the fetchVideos function with search parameter directly
      const sort = {
        field: "created_at",
        order: sortCriteria.value === "Newest" ? "desc" : "asc",
      };

      // Single API call with search parameter
      const response = await fetchVideos(page, search, sort);

      // Map videos with their headlines from the response
      const videosWithHeadlines = response.results.map((video) => ({
        ...video,
        headline: video.script_headline || `Script #${video.script}`,
      }));

      setVideos(videosWithHeadlines);
      setTotalVideos(response.total_items);
      setTotalPages(response.last_page);
      setPageSize(response.page_size);
    } catch (error) {
      console.error("Error loading videos:", error);
      handleAuthError(error);
      setError("Failed to load videos. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Load initial data or when page changes
    loadVideos(currentPage, searchText);
  }, [currentPage]); // Only react to currentPage changes

  // Handle search change with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    setCurrentPage(1);
    debouncedSearch(1, value);
  };

  // Handle sort change
  const handleSortChange = (sortOption) => {
    setSortCriteria(sortOption);
    setCurrentPage(1);
    loadVideos(1, searchText);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Function to handle video playback
  const handlePlay = (video) => {
    if (video.status === "completed" && video.download_url) {
      // Set the current video and open the player modal
      setCurrentVideo(video);
      setPlayerOpen(true);
    } else {
      alert("Video is still processing and cannot be played yet.");
    }
  };

  // Function to close the video player
  const handleClosePlayer = () => {
    setPlayerOpen(false);
    // Keep the current video in state for a moment in case user reopens
    // It will be cleared if they play a different video
  };

  // Function to handle video download
  const handleDownload = (video) => {
    if (video.status === "completed" && video.download_url) {
      // Create an anchor element and trigger download
      const a = document.createElement("a");
      a.href = video.download_url;
      a.download = `video_${video.id}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      alert("Video is still processing and cannot be downloaded yet.");
    }
  };

  // Function to handle video selection
  const handleVideoSelection = (video) => {
    setSelectedVideos((prev) => {
      // Convert to string for comparison if needed
      const videoId = video.id;
      const isSelected = prev.some((v) => v.id === videoId);

      if (isSelected) {
        return prev.filter((v) => v.id !== videoId);
      } else {
        return [...prev, video];
      }
    });
  };

  // Function to handle selecting/deselecting all videos
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Select all videos
      setSelectedVideos([...videos]);
    } else {
      // Deselect all videos
      setSelectedVideos([]);
    }
  };

  // Function to handle processing selected videos
  const handleProcessVideos = async () => {
    if (selectedVideos.length === 0) {
      alert("Please select at least one video to process");
      return;
    }

    setIsProcessing(true);
    const newProcessingStatus = { ...processingStatus };
    let processedCount = 0;
    let errorCount = 0;

    // Process each selected video
    for (const video of selectedVideos) {
      try {
        // Use video.video_id for processing status tracking
        const videoId = video.video_id || video.id;
        newProcessingStatus[videoId] = "processing";
        setProcessingStatus({ ...newProcessingStatus });

        // Construct the URL for the video status API endpoint using the API_ENDPOINTS constant
        const url = API_ENDPOINTS.VIDEO_STATUS(videoId);

        // Make the API request
        const response = await makeApiRequest(url, "GET");

        // Check if the response indicates success
        if (response && response.status === "completed") {
          newProcessingStatus[videoId] = "completed";
          processedCount++;
        } else {
          // Handle case where response exists but status is not completed
          newProcessingStatus[videoId] = "pending";
          processedCount++;
        }
      } catch (error) {
        console.error(`Error processing video:`, error);

        // Handle authentication errors
        handleAuthError(error);

        // Use video.video_id for processing status tracking
        const videoId = video.video_id || video.id;
        newProcessingStatus[videoId] = "error";
        errorCount++;
      }
    }

    setProcessingStatus({ ...newProcessingStatus });
    setIsProcessing(false);

    // Show notification of processing results
    setSnackbar({
      open: true,
      message: `Processed ${processedCount} videos${
        errorCount > 0 ? `, with ${errorCount} errors` : " successfully"
      }`,
      severity: errorCount > 0 ? "warning" : "success",
    });

    // Refresh the videos list after processing
    loadVideos(currentPage, searchText);
  };

  // Function to handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchText("");
    setCurrentPage(1);
    loadVideos(1, "");
  };

  // Handle search on Enter key
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setCurrentPage(1);
      loadVideos(1, searchText);
    }
  };

  // Function to handle confirming single video deletion
  const handleDeleteClick = (video) => {
    setDeleteDialog({
      open: true,
      videoId: video.id,
      videoTitle: `${video.script} - ${video.headline || "Untitled Script"}`,
      isBatch: false,
    });
  };

  // Function to handle confirming batch video deletion
  const handleBatchDeleteClick = () => {
    if (selectedVideos.length === 0) {
      alert("Please select at least one video to delete");
      return;
    }

    setDeleteDialog({
      open: true,
      videoId: null,
      videoTitle: "",
      isBatch: true,
    });
  };

  // Function to close delete confirmation dialog
  const handleDeleteDialogClose = () => {
    setDeleteDialog({
      ...deleteDialog,
      open: false,
    });
  };

  // Function to perform single video deletion
  const handleConfirmDelete = async () => {
    if (deleteDialog.isBatch) {
      await handleConfirmBatchDelete();
      return;
    }

    setIsDeleting(true);
    try {
      await deleteVideo(deleteDialog.videoId);

      // Update UI and close dialog
      setSnackbar({
        open: true,
        message: "Video deleted successfully",
        severity: "success",
      });

      // Remove the deleted video from the state - ensure proper type comparison
      setVideos((prevVideos) =>
        prevVideos.filter((video) => video.id !== deleteDialog.videoId)
      );

      // Also remove from selected videos if it was selected
      setSelectedVideos((prevSelected) =>
        prevSelected.filter((video) => video.id !== deleteDialog.videoId)
      );
    } catch (error) {
      console.error("Error deleting video:", error);
      handleAuthError(error);

      // Extract a more user-friendly error message
      let errorMessage = "Failed to delete video";

      if (error.message) {
        // Check if the error message is HTML (like from the 404 page)
        if (error.message.includes("<!DOCTYPE html>")) {
          errorMessage =
            "The API endpoint for deleting videos is not available";
        } else {
          errorMessage = `${errorMessage}: ${error.message}`;
        }
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
      handleDeleteDialogClose();
    }
  };

  // Function to perform batch video deletion
  const handleConfirmBatchDelete = async () => {
    setIsDeleting(true);
    try {
      const videoIds = selectedVideos.map((video) => video.id); // Use video.id

      const results = await batchDeleteVideos(videoIds);

      // Count successful and failed deletions
      const successCount = results.filter((r) => r.success).length;
      const failCount = results.filter((r) => !r.success).length;

      // Display results
      setSnackbar({
        open: true,
        message: `Deleted ${successCount} videos successfully${
          failCount > 0 ? `, ${failCount} failed` : ""
        }`,
        severity: failCount > 0 ? "warning" : "success",
      });

      // Filter out deleted videos from state
      const deletedIds = results.filter((r) => r.success).map((r) => r.videoId);

      // Filter out the videos that were successfully deleted
      setVideos((prevVideos) =>
        prevVideos.filter((video) => !deletedIds.includes(String(video.id)))
      );

      // Clear selection
      setSelectedVideos([]);
    } catch (error) {
      console.error("Error in batch delete:", error);
      handleAuthError(error);

      // Extract a more user-friendly error message
      let errorMessage = "Failed to delete videos";

      if (error.message) {
        // Check if the error message is HTML (like from the 404 page)
        if (error.message.includes("<!DOCTYPE html>")) {
          errorMessage =
            "The API endpoint for deleting videos is not available";
        } else {
          errorMessage = `${errorMessage}: ${error.message}`;
        }
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
      handleDeleteDialogClose();
    }
  };

  return (
    <Box sx={{ p: 3, pl: "24px", pr: "24px" }}>
      {/* Top section with heading and search */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          backgroundColor: "#fff",
          mt: "50px",
          padding: "44px",
        }}
      >
        {/* Left side - Title and subtitle */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold", fontSize: "25px" }}
          >
            Videos
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#888", mt: 0.5, textAlign: "left", fontSize: "16px" }}
          >
            All Videos
          </Typography>
        </Box>

        {/* Right side - Search and sort */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* Search field */}
          <Box sx={{ position: "relative", mr: 2 }}>
            <TextField
              size="small"
              placeholder="Search by Title ID"
              value={searchText}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
              sx={{
                width: 250,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "11.31px",
                  color: "#B5B7C0",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ ml: 1, mr: 1, color: "#7E7E7E", cursor: "pointer" }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <ClearIcon
                      sx={{ cursor: "pointer", color: "#7E7E7E" }}
                      onClick={handleClearSearch}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Sort component */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mr: "18px",
              ml: 1,
            }}
          >
            {/* <SortBy defaultValue="Newest" onSortChange={handleSortChange} /> */}
          </Box>

          {/* Process Videos Button */}
          <Button
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              mr: "18px",
              bgcolor: "#378dea",
              height: "42px",
              width: "239px",
              boxShadow: "none",
              padding: "10px",
              color: "#FFFFFF",
              "&:hover": {
                bgcolor: "#1565c0",
              },
              "&:disabled": {
                bgcolor: "#B0C4DE",
                color: "#FFFFFF",
              },
            }}
            onClick={handleProcessVideos}
            disabled={isProcessing || selectedVideos.length === 0}
          >
            {isProcessing
              ? "Processing..."
              : `Process Videos (${selectedVideos.length})`}
          </Button>

          {/* Delete Videos Button */}
          <Button
            variant="contained"
            endIcon={<DeleteIcon />}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              bgcolor: "#DF0404",
              height: "42px",
              boxShadow: "none",
              padding: "10px 20px",
              color: "#FFFFFF",
              "&:hover": {
                bgcolor: "#b30000",
              },
              "&:disabled": {
                bgcolor: "#FFC5C5",
                color: "#FFFFFF",
              },
            }}
            onClick={handleBatchDeleteClick}
            disabled={isDeleting || selectedVideos.length === 0}
          >
            {isDeleting ? "Deleting..." : `Delete (${selectedVideos.length})`}
          </Button>
        </Box>
      </Box>

      {/* Error message */}
      {error && (
        <Box sx={{ textAlign: "center", my: 4, color: "error.main" }}>
          <Typography variant="h6">{error}</Typography>
        </Box>
      )}

      {/* Loading indicator */}
      {loading && (
        <Box sx={{ textAlign: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Table of videos */}
      {!loading && !error && (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "8px",
            boxShadow: "none",
            border: "1px solid #eee",
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{ backgroundColor: "#fff", borderBottom: "1px solid #eee" }}
              >
                <TableCell
                  padding="checkbox"
                  sx={{ borderBottom: "1px solid #eee" }}
                >
                  <Checkbox
                    size="small"
                    onChange={handleSelectAll}
                    checked={
                      videos.length > 0 &&
                      selectedVideos.length === videos.length
                    }
                    indeterminate={
                      selectedVideos.length > 0 &&
                      selectedVideos.length < videos.length
                    }
                  />
                </TableCell>
                <TableCell
                  sx={{
                    width: "100px",
                    borderBottom: "1px solid #eee",
                  }}
                ></TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    borderBottom: "1px solid #eee",
                    color: "#B5B7C0",
                  }}
                >
                  Title
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    borderBottom: "1px solid #eee",
                    color: "#B5B7C0",
                  }}
                >
                  Date
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    borderBottom: "1px solid #eee",
                    color: "#B5B7C0",
                  }}
                >
                  ID
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    borderBottom: "1px solid #eee",
                    color: "#B5B7C0",
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    borderBottom: "1px solid #eee",
                    color: "#B5B7C0",
                    pl: "40px",
                  }}
                  colSpan={3}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {videos.map((video) => (
                <TableRow
                  key={video.id}
                  sx={{
                    "&:hover": { backgroundColor: "#f5f5f5" },
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <TableCell
                    padding="checkbox"
                    sx={{ borderBottom: "1px solid #eee" }}
                  >
                    <Checkbox
                      size="small"
                      checked={selectedVideos.some((v) => v.id === video.id)}
                      onChange={() => handleVideoSelection(video)}
                      disabled={
                        processingStatus[video.video_id || video.id] ===
                          "processing" ||
                        isProcessing ||
                        isDeleting
                      }
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid #eee" }}>
                    <Box
                      component="div"
                      sx={{
                        width: "75px",
                        height: "45px",
                        bgcolor: "#DDD",
                        borderRadius: "4px",
                        position: "relative",
                        overflow: "hidden",
                        cursor:
                          video.status === "completed" ? "pointer" : "default",
                      }}
                      onClick={() =>
                        video.status === "completed" && handlePlay(video)
                      }
                    >
                      {/* Display actual thumbnail for completed videos */}
                      {video.status === "completed" && video.thumbnail_url && (
                        <Box
                          component="img"
                          src={video.thumbnail_url}
                          alt={`Thumbnail for Script #${video.script}`}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      )}

                      {/* Thumbnail overlay with play icon */}
                      {video.status === "completed" && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(0,0,0,0.3)",
                            transition: "background-color 0.2s",
                            "&:hover": {
                              backgroundColor: "rgba(0,0,0,0.5)",
                            },
                          }}
                        >
                          <PlayArrowIcon
                            sx={{ color: "white", fontSize: 20 }}
                          />
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{ borderBottom: "1px solid #eee", color: "#555555" }}
                  >
                    <Box component="span" sx={{ fontWeight: "bold" }}>
                      {video.script}
                    </Box>
                    {` - ${video.headline || "Untitled Script"}`}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid #eee" }}>
                    {formatDate(video.created_at)}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: "1px solid #eee",
                      color: "#292D32",
                      fontWeight: "bold",
                    }}
                  >
                    {String(video.id).slice(0, 10)}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: "1px solid #eee",
                      color:
                        processingStatus[video.video_id || video.id] ===
                        "processing"
                          ? "#2196F3"
                          : processingStatus[video.video_id || video.id] ===
                            "pending"
                          ? "#0288D1"
                          : processingStatus[video.video_id || video.id] ===
                            "error"
                          ? "#F44336"
                          : video.status === "completed"
                          ? "#4CAF50"
                          : "#FFA000",
                      fontWeight: "bold",
                      textTransform: "capitalize",
                    }}
                  >
                    {processingStatus[video.video_id || video.id] ===
                    "processing" ? (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <CircularProgress
                          size={16}
                          sx={{ mr: 1, color: "#2196F3" }}
                        />
                        Processing
                      </Box>
                    ) : processingStatus[video.video_id || video.id] ===
                      "pending" ? (
                      "Pending"
                    ) : processingStatus[video.video_id || video.id] ===
                      "error" ? (
                      "Error"
                    ) : (
                      video.status
                    )}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ borderBottom: "1px solid #eee", width: "120px" }}
                  >
                    <Button
                      variant="contained"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => handlePlay(video)}
                      disabled={video.status !== "completed"}
                      sx={{
                        textTransform: "none",
                        borderRadius: "6px",
                        borderColor: "#B5B7C0",
                        backgroundColor:
                          video.status === "completed" ? "#4691E1" : "#cccccc",
                        paddingLeft: "10px",
                        paddingRight: "10px",
                        fontSize: "15.84px",
                        "&:hover": {
                          backgroundColor:
                            video.status === "completed"
                              ? "#3d7ec5"
                              : "#cccccc",
                        },
                        boxShadow: "none",
                      }}
                    >
                      Play
                    </Button>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ borderBottom: "1px solid #eee", width: "120px" }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<FileDownloadIcon />}
                      onClick={() => handleDownload(video)}
                      disabled={video.status !== "completed"}
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        color: video.status === "completed" ? "#333" : "#aaa",
                        borderColor:
                          video.status === "completed" ? "#e0e0e0" : "#eee",
                        fontSize: "15.84px",
                        paddingLeft: "10px",
                        paddingRight: "10px",
                        "&:hover": {
                          borderColor:
                            video.status === "completed" ? "#d0d0d0" : "#eee",
                          backgroundColor:
                            video.status === "completed" ? "#f5f5f5" : "",
                        },
                      }}
                    >
                      Download
                    </Button>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ borderBottom: "1px solid #eee", width: "100px" }}
                  >
                    <Button
                      variant="outlined"
                      endIcon={<DeleteIcon />}
                      onClick={() => handleDeleteClick(video)}
                      disabled={isDeleting}
                      sx={{
                        textTransform: "none",
                        borderRadius: "8px",
                        border: "1px solid #e0e0e0",
                        color: "#DF0404",
                        padding: "8px",
                        minWidth: "auto",
                        flexShrink: 0,
                        "&:hover": {
                          bgcolor: "#fff1f1",
                          border: "1px solid #DF0404",
                        },
                        "&.Mui-disabled": {
                          color: "#FFC5C5",
                          borderColor: "#ffebee",
                        },
                      }}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {!loading && !error && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: "#666" }}>
            Showing data{" "}
            {videos.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
            {Math.min(currentPage * pageSize, totalVideos)} of {totalVideos}{" "}
            entries
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              component="span"
              sx={{
                mx: 1,
                color: "#999",
                cursor: currentPage > 1 ? "pointer" : "default",
                opacity: currentPage > 1 ? 1 : 0.5,
                fontSize: "14px",
              }}
              onClick={() =>
                currentPage > 1 && setCurrentPage((prev) => prev - 1)
              }
            >
              &lt;
            </Typography>

            {Array.from({ length: Math.min(totalPages, 4) }).map((_, index) => (
              <Typography
                key={index}
                component="span"
                sx={{
                  mx: 1,
                  bgcolor: currentPage === index + 1 ? "#4691E1" : "#F5F5F5",
                  color: currentPage === index + 1 ? "white" : "#404B52",
                  borderRadius: "4px",
                  px: 1.5,
                  py: 0.5,
                  fontSize: "14px",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
                onClick={() => setCurrentPage(index + 1)}
              >
                {index + 1}
              </Typography>
            ))}

            {totalPages > 4 && (
              <>
                <Typography sx={{ mx: 1, color: "#999" }}>...</Typography>

                <Typography
                  component="span"
                  sx={{
                    mx: 1,
                    bgcolor: currentPage === totalPages ? "#4691E1" : "#F5F5F5",
                    color: currentPage === totalPages ? "white" : "#404B52",
                    borderRadius: "4px",
                    px: 1.5,
                    py: 0.5,
                    fontSize: "14px",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                  onClick={() => setCurrentPage(totalPages)}
                >
                  {totalPages}
                </Typography>
              </>
            )}

            <Typography
              component="span"
              sx={{
                mx: 1,
                color: "#999",
                cursor: currentPage < totalPages ? "pointer" : "default",
                opacity: currentPage < totalPages ? 1 : 0.5,
                fontSize: "14px",
              }}
              onClick={() =>
                currentPage < totalPages && setCurrentPage((prev) => prev + 1)
              }
            >
              &gt;
            </Typography>
          </Box>
        </Box>
      )}

      {/* Video Player Modal */}
      {currentVideo && (
        <VideoPlayerModal
          open={playerOpen}
          onClose={handleClosePlayer}
          videoUrl={currentVideo.download_url}
          videoTitle={`${currentVideo.script} - ${
            currentVideo.headline || "Untitled Script"
          }`}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        maxWidth="sm"
      >
        <DialogTitle
          id="delete-dialog-title"
          sx={{ fontWeight: "bold", color: "#DF0404" }}
        >
          {deleteDialog.isBatch
            ? `Delete ${selectedVideos.length} videos?`
            : "Delete Video?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {deleteDialog.isBatch
              ? `Are you sure you want to delete ${selectedVideos.length} selected videos? This action cannot be undone.`
              : `Are you sure you want to delete "${deleteDialog.videoTitle}"? This action cannot be undone.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleDeleteDialogClose}
            disabled={isDeleting}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              border: "1px solid #e0e0e0",
              color: "#666",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={
              isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />
            }
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              bgcolor: "#DF0404",
              height: "42px",
              boxShadow: "none",
              color: "#FFFFFF",
              "&:hover": {
                bgcolor: "#b30000",
              },
            }}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Videos;
